class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0, event);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      return this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        event,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      return fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');

          // Update items content (innerHTML instead of replaceWith to keep element reference)
          const targetItems = document.querySelector('.rt-items');
          const sourceItems = html.querySelector('.rt-items');
          if (targetItems && sourceItems) {
            targetItems.innerHTML = sourceItems.innerHTML;
          }

          // Update footer content
          const targetFooter = document.querySelector('.cart-drawer__footer');
          const sourceFooter = html.querySelector('.cart-drawer__footer');
          if (targetFooter && sourceFooter) {
            targetFooter.innerHTML = sourceFooter.innerHTML;
          }

          // Update header content
          const targetHeader = document.querySelector('.rt-header');
          const sourceHeader = html.querySelector('.rt-header');
          if (targetHeader && sourceHeader) {
            targetHeader.innerHTML = sourceHeader.innerHTML;
          }

          // Update is-empty state on cart-drawer
          const cartDrawer = document.querySelector('cart-drawer');
          const newCartDrawer = html.querySelector('cart-drawer');
          if (cartDrawer && newCartDrawer) {
            if (newCartDrawer.classList.contains('is-empty')) {
              cartDrawer.classList.add('is-empty');
            } else {
              cartDrawer.classList.remove('is-empty');
            }
          }

          // Update cart-drawer-items is-empty state
          const cartDrawerItems = document.querySelector('cart-drawer-items');
          const newCartDrawerItems = html.querySelector('cart-drawer-items');
          if (cartDrawerItems && newCartDrawerItems) {
            if (newCartDrawerItems.classList.contains('is-empty')) {
              cartDrawerItems.classList.add('is-empty');
            } else {
              cartDrawerItems.classList.remove('is-empty');
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      return fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  /**
   * Shopify can sometimes split a same variant into multiple lines (same variant, same properties)
   * after discounts/stock adjustments. When the lines are mergeable, we consolidate them back into
   * a single line so the customer sees "Qté = 4" instead of "3 + 1".
   *
   * We only attempt this when:
   * - same variant_id
   * - same selling plan (or none)
   * - same properties
   * - same final_price (so we don't break mixed pricing/discount allocations)
   */
  rtFindMergeableDuplicateLineGroups(items = []) {
    const groups = new Map();
    items.forEach((item, idx) => {
      const variantId = item && item.variant_id;
      if (!variantId) return;
      const sellingPlanId = item.selling_plan_allocation?.selling_plan?.id || '';
      const props = item.properties || {};
      const propsKey = JSON.stringify(props);
      const finalPrice = item.final_price ?? item.price ?? '';
      const key = `${variantId}::${sellingPlanId}::${finalPrice}::${propsKey}`;
      const line = idx + 1; // line numbers are 1-based
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push({ line, quantity: item.quantity || 0 });
    });

    const duplicates = [];
    for (const [, lines] of groups) {
      if (lines.length > 1) duplicates.push(lines);
    }
    return duplicates;
  }

  async rtConsolidateDuplicateLines(duplicateGroups = []) {
    // Prevent loops if Shopify re-splits due to discount logic.
    if (this.__rtConsolidating) return false;
    this.__rtConsolidating = true;

    try {
      for (const group of duplicateGroups) {
        if (!group || group.length < 2) continue;

        // Keep the lowest line number to minimize shifting.
        const sorted = [...group].sort((a, b) => a.line - b.line);
        const keep = sorted[0];
        // Remove in descending order so line numbers don't shift underneath us.
        const others = sorted.slice(1).sort((a, b) => b.line - a.line);
        const totalQty = group.reduce((sum, g) => sum + (g.quantity || 0), 0);
        if (!keep.line || totalQty <= 0) continue;

        // 1) Set keep line to total quantity
        await fetch(`${routes.cart_change_url}`, {
          ...fetchConfig(),
          body: JSON.stringify({ line: keep.line, quantity: totalQty }),
        });

        // 2) Remove the other duplicate lines
        for (const o of others) {
          if (!o.line) continue;
          await fetch(`${routes.cart_change_url}`, {
            ...fetchConfig(),
            body: JSON.stringify({ line: o.line, quantity: 0 }),
          });
        }
      }
      return true;
    } catch (e) {
      console.error('Error consolidating duplicate cart lines:', e);
      return false;
    } finally {
      this.__rtConsolidating = false;
    }
  }

  updateQuantity(line, quantity, event, name, variantId) {
    const eventTarget = event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';
    const cartPerformanceUpdateMarker = CartPerformance.createStartingMarker(`${eventTarget}:user-action`);

    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then(async (response) => {
        const state = await response.text();
        return { response, state };
      })
      .then(({ response, state }) => {
        let parsedState;
        try {
          parsedState = JSON.parse(state);
        } catch (e) {
          throw e;
        }

        CartPerformance.measure(`${eventTarget}:paint-updated-sections`, () => {
          const quantityElement =
            document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
          const items = document.querySelectorAll('.cart-item');

          // Shopify AJAX API uses 422 status for stock/quantity issues and returns { status, message, description }.
          // Dawn expects parsedState.errors + sections; handle 422 explicitly to avoid UI desync/duplicate lines.
          if (response.status === 422 || parsedState.status === 422) {
            if (quantityElement) quantityElement.value = quantityElement.getAttribute('value');
            const message = parsedState.description || parsedState.message || window.cartStrings.error;
            this.updateLiveRegions(line, message);
            if (typeof window.rtShowCartStockModal === 'function') {
              window.rtShowCartStockModal(message);
            }
            // Refresh DOM to reflect the real server-side cart state.
            this.onCartUpdate();
            return;
          }

          if (parsedState.errors) {
            quantityElement.value = quantityElement.getAttribute('value');
            this.updateLiveRegions(line, parsedState.errors);
            return;
          }

          // If Shopify split the same variant into multiple identical lines, consolidate them.
          // We do this after a successful change, before rendering, to avoid flashing "3 + 1".
          const dupGroups = this.rtFindMergeableDuplicateLineGroups(parsedState.items || []);
          if (dupGroups.length) {
            // Note: CartPerformance.measure expects sync; we kick off async consolidation and then refresh.
            this.rtConsolidateDuplicateLines(dupGroups).then(() => this.onCartUpdate());
          }

          this.classList.toggle('is-empty', parsedState.item_count === 0);
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          const cartFooter = document.getElementById('main-cart-footer');

          if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
          if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

          this.getSectionsToRender().forEach((section) => {
            const elementToReplace =
              document.getElementById(section.id).querySelector(section.selector) ||
              document.getElementById(section.id);
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          });
          const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
          let message = '';
          if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
            if (typeof updatedValue === 'undefined') {
              message = window.cartStrings.error;
            } else {
              message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
            }
          }
          this.updateLiveRegions(line, message);

          const lineItem =
            document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
          if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
            cartDrawerWrapper
              ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
              : lineItem.querySelector(`[name="${name}"]`).focus();
          } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
          } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
          }
        });

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
        CartPerformance.measureFromMarker(`${eventTarget}:user-action`, cartPerformanceUpdateMarker);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } }).then(() =>
              CartPerformance.measureFromEvent('note-update:user-action', event)
            );
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
